import { hideScrollBar } from '@byecode/ui'
import {
    type AutoLayout,
    EVENT_TYPE,
    type LayoutSize,
    ScrollEventProtocol,
    type TimerEventProtocol,
    type VariableADTvalue
} from '@lighthouse/core'
import type { DependentDataContextValue, FlowLayoutNode } from '@lighthouse/shared'
import {
    combineBackgroundStyle,
    DependentDataProvider,
    DragPlugin,
    fieldConvertValue,
    findNodeDom,
    getBackgroundStyle,
    getVeinsStyle,
    LayoutEngine,
    LayoutInteractionEngine,
    PAGE_SCROLL_PARENT_CONTENT,
    PageContainerProvider,
    ResizePlugin,
    transformBlock2FlowLayoutNode,
    useActionRunningLoadings,
    useAppContainerContext,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useFillPickerContext,
    useManualSetMediaQueryRemBase,
    usePageTimerEvent
} from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useLatest, useMount, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { applicationSettingAtom, appRoleAtom, transformSyncComponentsAtom } from '@/atoms/application/state'
import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    containerDesignStateAtom,
    containerRuntimeStateAtomFamily,
    lastPageOfStackAtom,
    pageAtomFamily,
    pageBlocksAtom,
    pageInternalStateAtomFamily,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStackAtomFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import { AsideType } from '@/atoms/page/types'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useClickAwayOutside } from '@/hooks/layoutEngine/useClickAwayOutside'
import { useSelectionNodesActions } from '@/hooks/layoutEngine/useSelectionNodesActions'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useScrollEvent } from '@/hooks/useScrollEvent'
import { useVisibilityFilter } from '@/hooks/useVisibilityFilter'
import type { FlowLayoutEvents } from '@/utils/flowLayoutEventBus'
import { flowLayoutEmitter } from '@/utils/flowLayoutEventBus'
import { usePageUndoRedo } from '@/utils/undoRedo/page'

import { PageSuspendPagination } from '../PageSuspendPagination'
import { BlockRender } from './BlockRender'
import { BlockToolbar } from './BlockToolbar'
import { useLayoutEngineEvents } from './useLayoutEngineInteract'

const PageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`

const ScrollerArea = styled.div`
    /* background-color: var(--color-white); */
    overflow: hidden auto;
    width: 100%;
    height: 100%;
    ${hideScrollBar}
`

interface PageContentProps {
    loading?: boolean
}

export const PageContent: React.FC<PageContentProps> = () => {
    const pageRef = useRef<HTMLDivElement>(null)
    const { pageId } = useCurrentPageContext()
    const stackId = useCurrentStackIdContext()
    const endPoint = useManualSetMediaQueryRemBase()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const userId = useAtomData(appRoleAtom)
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { handleActionListTrigger } = useActionTrigger()
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const { scale, disabledPageScroll } = useAppContainerContext()
    const dataSourceList = useDataSourceList(appId, envId)

    const pageDesign = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.breakPoint?.design, [])
    )

    const pageLayout = useAtomData(
        pageAtomFamily(pageId),
        useCallback(
            s => ({
                layout:
                    s?.breakPoint?.layout ||
                    ({
                        align: { direction: 'vertical' },
                        gap: { unit: 'px', size: 12 },
                        padding: { size: [10, 10, 10, 10], unit: 'px' }
                    } satisfies AutoLayout),
                size: s?.breakPoint?.size || ({ width: { size: 'fill' }, height: { size: 'auto' } } satisfies LayoutSize)
            }),
            []
        )
    )

    const pageEventList = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.eventList, [])
    )

    const scrollBottomEvent = useMemo(
        () => pageEventList?.find(item => item.type === EVENT_TYPE.SCROLL_BOTTOM) as ScrollEventProtocol,
        [pageEventList]
    )

    const loadEvent = useMemo(() => pageEventList?.find(item => item.type === EVENT_TYPE.LOAD), [pageEventList])
    const timerEvent = useMemo(
        () => pageEventList?.find(item => item.type === EVENT_TYPE.TIMER) as TimerEventProtocol | undefined,
        [pageEventList]
    )

    const isPageNavbar = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.isShowNavbar, [])
    )

    const navbarDependencyList = useAtomData(
        applicationSettingAtom,
        useCallback(
            s => [
                s?.navbar.height,
                s?.navbar.isShow,
                s?.navbar.padding,
                s?.navbar.topAndDownPadding,
                s?.navbar?.topPadding,
                s?.navbar?.isUpdatePageShowNavbar
            ],
            []
        )
    )

    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const pageStackDisplayType = useAtomData(
        pageStackAtomFamily(stackId),
        useCallback(s => s?.pageStackDisplayType, [])
    )

    // ✅ 优化：使用回调函数按需获取容器状态，避免传递整个聚合状态 map
    const getContainerState = useAtomCallback(
        useCallback(
            (get, set, containerId: string) => get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined,
            [stackId]
        )
    )
    const { selectedNodes, asideType } = useAtomData(pageInternalStateAtomFamily(stackId))
    const singleSelectedNode = selectedNodes && selectedNodes.length === 1 ? selectedNodes[0] : undefined

    const syncComponents = useAtomData(transformSyncComponentsAtom)
    // 右侧栏改配置/文案时 pageBlocks 每次都会变，用 deferred 避免每次按键都重算 transform，减轻打字卡顿
    const deferredPageBlocks = useDeferredValue(pageBlocks)
    const flowNodes = useMemo(
        () => transformBlock2FlowLayoutNode({ blocks: deferredPageBlocks, syncComponents }),
        [deferredPageBlocks, syncComponents]
    )

    /** 当前页面是栈顶 */
    const isCurrentStackTop = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId === pageId && s?.stackId === stackId, [pageId, stackId])
    )

    const ref = useRef<HTMLDivElement>(null)

    const disabledWithVersion = useIsDisabledWithVersion()

    useClickAwayOutside({ ref, enabled: isCurrentStackTop })

    useMount(() => {
        if (!loadEvent) {
            return
        }
        handleActionTriggerWithLoading({
            actorEvent: loadEvent,
            id: stackId,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent)
        })
    })

    useScrollEvent({ ref: pageRef, scrollBottomEvent: scrollBottomEvent })
    usePageTimerEvent({
        timerEvent,
        stackId,
        trigger: handleActionListTrigger
    })

    usePageUndoRedo(
        {
            enabled: isCurrentStackTop && !disabledWithVersion,
            filter(event) {
                return !(event.target instanceof HTMLInputElement && (!event.target.type || event.target.type === 'text'))
            },
            filterPreventDefault: false,
            enableOnTags: ['INPUT']
        },
        [stackId]
    )

    /** ********************************* 栅格的交互事件 start ******************************** */
    // 选中节点
    const { run: setPageInternalState } = useAtomAction(pageInternalStateAtomFamily(stackId))
    const onSelectedIdChange = useCallback(
        (ids: NodeIdWithScope[]) => {
            if (!isCurrentStackTop) {
                return
            }
            setPageInternalState(draft => {
                draft.selectedNodes = ids
                draft.asideType = ids.length === 1 ? AsideType.BLOCK : AsideType.PAGE
            })
        },
        [isCurrentStackTop, setPageInternalState]
    )

    // 复制粘贴节点
    useSelectionNodesActions({
        enabled: isCurrentStackTop,
        ref
    })

    const instanceRef = useRef<LayoutInteractionEngine<DragPlugin | ResizePlugin> | null>(null)
    const dragPluginRef = useRef<DragPlugin | null>(null)
    const { collisionAreaDetection, onDragEnd, getResizeRestrict, onResizeEnd } = useLayoutEngineEvents()
    const latestEvents = useLatest({
        onSelectedIdChange,
        collisionAreaDetection,
        onDragEnd,
        getResizeRestrict,
        onResizeEnd
    })

    const mergeRootNodes = useMemo<FlowLayoutNode[]>(
        () => [
            {
                id: 'root',
                type: 'container',
                data: {
                    size: pageLayout.size,
                    layout: pageLayout.layout,
                    design: pageDesign
                },
                children: flowNodes
            }
        ],
        [flowNodes, pageDesign, pageLayout.layout, pageLayout.size]
    )

    useLayoutEffect(() => {
        const el = ref.current
        if (!el) {
            return
        }

        const appContainerDom = document.querySelector('.applicationContainer')?.parentElement?.parentElement
        if (!appContainerDom) {
            throw new Error('.applicationContainer dom was gone')
        }

        if (instanceRef.current) {
            instanceRef.current.updateOption({
                scale,
                data: mergeRootNodes,
                selectedIds: isCurrentStackTop ? selectedNodes : [],
                // getContainerState,
                onSelect: latestEvents.current.onSelectedIdChange,
                notSelectPage: asideType === AsideType.NAVBAR
            })
        } else {
            const dragPlugin = new DragPlugin({
                onDragEnd: latestEvents.current.onDragEnd,
                collisionAreaDetection: latestEvents.current.collisionAreaDetection
            })
            const resizePlugin = new ResizePlugin({
                getResizeRestrict: latestEvents.current.getResizeRestrict,
                // onResizeStart: latestEvents.current.onResizeEnd,
                onResizeEnd: latestEvents.current.onResizeEnd
            })

            const layoutInteractionEngine = new LayoutInteractionEngine({
                data: mergeRootNodes,
                root: el,
                boundary: appContainerDom,
                scale,
                selectedIds: isCurrentStackTop ? selectedNodes : [],
                getContainerState,
                onSelect: latestEvents.current.onSelectedIdChange,
                plugins: [dragPlugin, resizePlugin]
            })

            dragPluginRef.current = dragPlugin
            instanceRef.current = layoutInteractionEngine
        }
    }, [scale, mergeRootNodes, latestEvents, selectedNodes, isCurrentStackTop, asideType, pageStackDisplayType, getContainerState])

    useLayoutEffect(() => {
        return () => {
            instanceRef.current?.destroy()
            instanceRef.current = null
        }
    }, [])

    useUpdateEffect(() => {
        instanceRef.current?.collectNodes()
    }, [navbarDependencyList, isPageNavbar])

    useEffect(() => {
        function beginAdding(e: FlowLayoutEvents['beginAdding']) {
            if (!isCurrentStackTop) {
                return
            }
            dragPluginRef.current?.startDrag(e)
        }
        // 栅格监听从外部添加node
        flowLayoutEmitter.on('beginAdding', beginAdding)

        return () => flowLayoutEmitter.off('beginAdding', beginAdding)
    }, [isCurrentStackTop])

    /** ********************************* 栅格的交互事件 end ******************************** */

    const containerDesignState = useAtomData(
        containerDesignStateAtom,
        useCallback(
            s => {
                return {
                    ...s,
                    root: s[pageId]
                }
            },
            [pageId]
        )
    )

    const blockHighlight = useAtomData(blockHighlightAtom)
    const highlight = useMemo(() => {
        return {
            ...containerDesignState,
            ...Object.fromEntries(blockHighlight.map(id => [id, { self: true }]))
        }
    }, [containerDesignState, blockHighlight])

    /** **************************** 解析背景图片 start **************************** */

    const background = pageDesign?.background
    const { palettes } = useFillPickerContext()
    const parseVariableImage = useParseVariableWithSubscription(background?.image?.resource)
    const parseBackgroundVariableImage = useCallback(
        (value: VariableADTvalue | undefined) => {
            const field = parseVariableImage(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariableImage]
    )
    const parseVariableColor = useParseVariableWithSubscription(background?.variable)
    const parseBackgroundVariableColor = useCallback(
        (value: VariableADTvalue | undefined) => {
            const field = parseVariableColor(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseVariableColor]
    )
    const backgroundStyle: React.CSSProperties = useMemo(() => {
        return {
            ...combineBackgroundStyle([
                getBackgroundStyle({
                    appId,
                    value: background,
                    palettes,
                    parseVariableImage: parseBackgroundVariableImage,
                    parseVariableColor: parseBackgroundVariableColor
                }),
                getVeinsStyle(pageDesign?.veins, palettes)
            ])
        }
    }, [appId, background, pageDesign?.veins, palettes, parseBackgroundVariableImage, parseBackgroundVariableColor])

    const layerLayoutStyle = useMemo(() => {
        return {
            ...backgroundStyle,
            backgroundAttachment: 'fixed'
        }
    }, [backgroundStyle])
    /** **************************** 解析背景图片 end ****************************** */

    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const dependentData: DependentDataContextValue = useMemo(
        () => ({
            dataSourceList,
            personOptions,
            roleOptions,
            departmentOptions,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            pageVariableSchemas,
            pagePathParameter,
            pageQueryParameter,
            userId
        }),
        [
            dataSourceList,
            departmentOptions,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            pagePathParameter,
            pageQueryParameter,
            pageVariableSchemas,
            personOptions,
            roleOptions,
            userId
        ]
    )

    return (
        <PageContainer>
            <PageContainerProvider endpoint={endPoint} dragPlugin={instanceRef.current}>
                <ScrollerArea id={PAGE_SCROLL_PARENT_CONTENT} ref={pageRef}>
                    <DependentDataProvider value={dependentData}>
                        <LayoutEngine
                            ref={ref}
                            data={flowNodes}
                            size={pageLayout?.size}
                            layout={pageLayout?.layout}
                            design={pageDesign}
                            style={layerLayoutStyle}
                            selectedNodes={selectedNodes}
                            highlight={highlight}
                            scale={scale}
                            sortable
                            nodeRender={BlockRender}
                            useParseVariableWithSubscription={useParseVariableWithSubscription}
                            useVisibilityFilter={useVisibilityFilter}
                        />
                    </DependentDataProvider>

                    {isCurrentStackTop && singleSelectedNode && (
                        <BlockToolbar
                            selectedNode={singleSelectedNode}
                            onChangeSelectedId={onSelectedIdChange}
                            onDragBar={e => {
                                dragPluginRef.current?.startDrag(
                                    e.nativeEvent,
                                    singleSelectedNode,
                                    (ref.current && findNodeDom(singleSelectedNode, ref.current)) || undefined
                                )
                            }}
                        />
                    )}
                </ScrollerArea>
                {/* 悬浮视图工具栏 */}
                <PageSuspendPagination />
            </PageContainerProvider>
        </PageContainer>
    )
}
